import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Facebook, Menu, X, Info, Building2, Landmark, HomeIcon } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  A.C. Rivero Development Corporation — concept site. Developer + construction
  hybrid identity. No project photography was available in the research, so the
  design leans entirely on restrained gold/cream typography and its real
  government/housing project history rather than any imagery beyond the logo.
*/

const GOLD = "#B08A3E";
const NAVY = "#111827";
const CREAM = "#F8F6EF";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/acriverodevcorp/";
const NAME = "A.C. Rivero Development Corporation";
const SLUG = "ac-rivero-development-concept";
const P = "/construction/acrivero";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function AcRiveroConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["What We Do", "#capabilities"], ["Project History", "#history"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Cormorant Garamond', serif" }} data-testid="acrivero-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cardo:wght@400;700&family=Jost:wght@400;500;600&display=swap');
        .acr-sans { font-family: 'Jost', sans-serif; }
        #top h1, #top h2, #top h3 { font-family: 'Cardo', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#0B0F17] text-white text-center text-xs py-2 relative z-50 acr-sans">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow acr-sans ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="acr-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.png`} alt={NAME} className="h-11 w-11 object-contain" />
            <span className="font-medium tracking-wide" style={{ color: NAVY }}>A.C. RIVERO</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="acr-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-semibold text-white" style={{ background: NAVY }}>
            Discuss a Project
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="acr-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white acr-sans">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="acr-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold text-white" style={{ background: NAVY }}>Discuss a Project</a>
          </div>
        )}
      </header>

      {/* HERO — typography-led, no project photography available */}
      <section className="relative py-12 md:py-16" style={{ background: NAVY }} data-testid="acr-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_.46fr] border" style={{ borderColor: `${GOLD}55` }}>
          <motion.div {...fadeUp} className="p-8 sm:p-12 lg:p-16">
            <img src={`${P}/logo.png`} alt={NAME} className="h-20 mb-8" />
            <span className="acr-sans text-xs font-medium uppercase tracking-[0.3em]" style={{ color: GOLD }}>Bacolod City, Negros Occidental</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.15] text-white font-semibold">Development &amp; Construction, Together.</h1>
            <p className="mt-6 acr-sans text-lg text-white/80 leading-relaxed max-w-2xl">
              A development and construction corporation with a history in government infrastructure and housing projects across Negros Occidental.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4 acr-sans">
              <a href="#contact" data-testid="acr-hero-cta" className="rounded-sm px-7 py-3.5 text-sm font-semibold text-white" style={{ background: GOLD }}>Discuss a Project</a>
              <a href="#history" data-testid="acr-hero-history" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Project History</a>
            </div>
          </motion.div>
          <aside className="p-8 sm:p-10 flex flex-col justify-end" style={{ background: "#EFE7D3" }}>
            <p className="acr-sans text-xs uppercase tracking-[.2em]" style={{ color: GOLD }}>Documented work</p>
            <div className="mt-6 space-y-0 acr-sans text-sm" style={{ color: NAVY }}><p className="border-t py-4" style={{ borderColor: `${GOLD}55` }}>Government &amp; Infrastructure</p><p className="border-t py-4" style={{ borderColor: `${GOLD}55` }}>Housing Development</p><p className="border-y py-4" style={{ borderColor: `${GOLD}55` }}>General Construction</p></div>
          </aside>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="acr-sans text-xs font-medium uppercase tracking-[0.3em]" style={{ color: GOLD }}>What We Do</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-semibold leading-snug">Capabilities</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10 acr-sans">
          {[
            [Landmark, "Government & Infrastructure", "Public infrastructure work, including road and drainage projects for local government units."],
            [HomeIcon, "Housing Development", "Socialized and community housing development projects."],
            [Building2, "General Construction", "Construction services for commercial, institutional and community clients."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: GOLD }} />
              <h3 className="mt-4 text-lg font-medium" style={{ color: NAVY }}>{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROJECT HISTORY */}
      <section id="history" className="py-20 md:py-28" style={{ background: "#EFE7D3" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="mb-10">
            <span className="acr-sans text-xs font-medium uppercase tracking-[0.3em]" style={{ color: GOLD }}>Project History</span>
            <h2 className="mt-3 text-4xl font-semibold leading-snug">Selected Public &amp; Development Work</h2>
          </motion.div>
          <motion.ul {...fadeUp} className="acr-sans space-y-4 text-lg" style={{ color: `${INK}CC` }}>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Murcia Heights socialized housing development, Murcia, Negros Occidental</li>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Pulupandan Pier Road rehabilitation · ₱45.76M · 91.66% “Very Satisfactory” final rating</li>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Bacolod Museum, Brgy. Tangub · ₱96.5M · 94.40% CPES visit score</li>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Binalbagan River Bank slope protection · ₱96.38M public-works record</li>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Iloilo City access road and bike lane · ₱48.90M public-works record</li>
            <li className="border-t pt-4" style={{ borderColor: `${GOLD}55` }}>Victorias City New Government Center, Victorias City</li>
          </motion.ul>
          <p className="acr-sans mt-6 text-xs" style={{ color: `${INK}88` }}>Project values and performance figures are drawn from published government CPES records and should be confirmed before contracting.</p>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp} className="acr-sans">
            <span className="text-xs font-medium uppercase tracking-[0.3em]" style={{ color: GOLD }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-semibold leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Discuss a Project</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Bacolod City, Negros Occidental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="acr-facebook-link" className="hover:underline">facebook.com/acriverodevcorp</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="acr"
              accent={GOLD}
              accentText="#FFFFFF"
              heading="Discuss a Project"
              subheading="Government, housing or general construction — tell us about the project and our team will follow up."
              projectTypes={["Government / Infrastructure", "Housing Development", "General Construction", "Other"]}
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14 acr-sans" style={{ background: NAVY, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.png`} alt={NAME} className="h-10 w-10 object-contain" />
              <span className="text-white text-base font-medium tracking-wide">A.C. RIVERO DEVELOPMENT</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Development and construction based in Bacolod City, Negros Occidental.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Bacolod City, Negros Occidental</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl acr-sans" style={{ background: NAVY }}>
        Message A.C. Rivero
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
